import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { AuthService } from '../../services/auth.service';
import { AuthStateService } from '../../services/auth-state.service';
import { environment } from '@env/environment';
import { Microservice } from '@hypenation/shared';
import { switchMap } from 'rxjs';

@Component({
  selector: 'hypenation-auth-login-with-hac-membership',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzCardModule,
    NzSpinModule,
  ],
  templateUrl: './login-with-hac-membership.component.html',
  styleUrl: './login-with-hac-membership.component.css',
})
export class LoginWithHacMembershipComponent {
  membershipForm: FormGroup;
  otpForm: FormGroup;
  step = signal<'membership' | 'otp'>('membership');
  loading = signal(false);
  membershipNumber = signal<string>('');
  userEmail = signal<string>('');
  bannerImage = signal<string>('images/hac-banner.jpg');
  private readonly apiUrl = environment.apiUrl;

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _authService: AuthService,
    private readonly _notification: NzNotificationService,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _authStateService: AuthStateService,
    private readonly _http: HttpClient
  ) {
    this.membershipForm = this._fb.group({
      hacMemberNumber: ['', [Validators.required]],
    });

    this.otpForm = this._fb.group({
      otp: ['', [Validators.required, Validators.minLength(4)]],
    });
  }

  onMembershipSubmit(): void {
    if (this.membershipForm.valid) {
      this.loading.set(true);
      const hacMemberNumber = this.membershipForm.get('hacMemberNumber')?.value;
      this.membershipNumber.set(hacMemberNumber);

      this._authService
        .initiateAuthentication({ hacMemberNumber })
        .pipe(
          switchMap((otpRes: HttpResponse<any>) => {
            if (otpRes.body?.success) {
              // After successful OTP request, fetch user details by HAC number
              const endpoint = `${this.apiUrl}/${Microservice.CRM}/opn/v1/hac-members/member-number/${hacMemberNumber}/details`;
              const httpOptions = {
                headers: new HttpHeaders({
                  Accept: '*/*',
                }),
                observe: 'response' as const,
              };
              return this._http.get<any>(endpoint, httpOptions);
            } else {
              throw new Error('Failed to send OTP');
            }
          })
        )
        .subscribe({
          next: (userRes: HttpResponse<any>) => {
            // Extract email from user details
            const email = userRes.body?.email || userRes.body?.user?.email;
            if (email) {
              this.userEmail.set(email);
              this._notification.success(
                'OTP Sent',
                'We\'ve sent an OTP to your email. Please check your inbox.'
              );
              this.step.set('otp');
              this.loading.set(false);
            } else {
              this._notification.error(
                'Failed to Get User Details',
                'Could not retrieve email address. Please try again.'
              );
              this.loading.set(false);
            }
          },
          error: (err) => {
            console.error('Initiate auth or get user details error:', err);
            this._notification.error(
              'Failed to Send OTP',
              'Failed to send OTP. Please try again.'
            );
            this.loading.set(false);
          },
        });
    } else {
      Object.values(this.membershipForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  onOtpSubmit(): void {
    if (this.otpForm.valid) {
      this.loading.set(true);
      const otp = this.otpForm.get('otp')?.value;
      const email = this.userEmail();

      if (!email) {
        this._notification.error(
          'Email Not Found',
          'Email address not available. Please try again.'
        );
        this.loading.set(false);
        return;
      }

      this._authService
        .authenticate({ email, otp })
        .subscribe({
          next: (res: HttpResponse<any>) => {
            if (res.body?.accessToken) {
              // Store tokens using app-specific keys
              const refreshToken = res.body.refreshToken;
              if (refreshToken) {
                this._authStateService.setTokens(res.body.accessToken, refreshToken);
              } else {
                this._authStateService.setToken(res.body.accessToken);
              }

              // Store HAC member number in session storage
              const memberNumber = this.membershipNumber();
              if (memberNumber) {
                this._authStateService.setHacMemberNumber(memberNumber);
              }

              this._notification.success(
                'Login Successful',
                'You have been successfully authenticated!'
              );
              
              // Redirect to returnUrl if provided, otherwise to tickets
              const returnUrl = this._route.snapshot.queryParams['returnUrl'] || '/tickets';
              this._router.navigate([returnUrl]);
            } else {
              this._notification.error(
                'Invalid OTP',
                'The OTP code you entered is invalid. Please try again.'
              );
              this.loading.set(false);
            }
          },
          error: (err) => {
            console.error('Authenticate error:', err);
            this._notification.error(
              'Authentication Failed',
              'Invalid OTP. Please try again.'
            );
            this.loading.set(false);
          },
        });
    } else {
      Object.values(this.otpForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  goBack(): void {
    this.step.set('membership');
    this.otpForm.reset();
    this.userEmail.set('');
  }
}

